import { GenericTable } from "@shared/components/GenericTable";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { TableAction, TableColumn } from "@shared/types/table";
import { Button } from "@shared/ui/button";
import { Checkbox } from "@shared/ui/checkbox";
import { PAGE_SIZE } from "@shared/utils/constants";
import { X } from "lucide-react";
import { useState } from "react";

interface GeneratedBill {
  id: string;
  invoiceNo: string;
  consumerName: string;
  accountNumber: string;
  consumption: number;
  billAmount: number;
  outstanding: number;
  totalDue: number;
}

interface GeneratedBillsModalProps {
  open: boolean;
  onClose: () => void;
  bills: GeneratedBill[];
  onApprove: (bills: GeneratedBill[]) => void;
}

// Mock data generator
const generateMockBills = (count: number): GeneratedBill[] => {
  const names = [
    "John Smith",
    "Sarah Johnson",
    "Michael Brown",
    "Emily Davis",
    "David Wilson",
  ];

  return Array.from({ length: count }, (_, index) => {
    const consumption = Math.floor(Math.random() * 500) + 200;
    const billAmount = Number((consumption * 2.5).toFixed(2));
    const outstanding =
      Math.random() > 0.5 ? Number((Math.random() * 500).toFixed(2)) : 0;
    const totalDue = Number((billAmount + outstanding).toFixed(2));

    return {
      id: `bill-${index + 1}`,
      invoiceNo: `INV-2024-${String(index + 1).padStart(3, "0")}`,
      consumerName: names[index % names.length],
      accountNumber: `ACC-${String(index + 1).padStart(6, "0")}`,
      consumption,
      billAmount,
      outstanding,
      totalDue,
    };
  });
};

export const GeneratedBillsModal = ({
  open,
  onClose,
  bills: initialBills,
  onApprove,
}: GeneratedBillsModalProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [checklist, setChecklist] = useState({
    calculations: true,
    accuracy: true,
    consent: true,
  });

  const CURRENCY = useCurrency();

  // Use provided bills or generate mock data
  const bills = initialBills.length > 0 ? initialBills : generateMockBills(5);

  const handleChecklistChange = (key: keyof typeof checklist) => {
    setChecklist((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleView = (bill: GeneratedBill) => {
    console.log("View bill:", bill);
    // Navigate to bill detail or open preview
  };

  const handleDownload = (bill: GeneratedBill) => {
    console.log("Download bill:", bill);
    // Trigger download
  };

  const handleMoreActions = (bill: GeneratedBill) => {
    console.log("More actions for bill:", bill);
    // Show additional actions menu
  };

  const handleApprove = () => {
    const allChecked = Object.values(checklist).every((val) => val);
    if (!allChecked) {
      alert("Please complete all checklist items before approving");
      return;
    }
    onApprove(bills);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Define columns
  const columns: TableColumn<GeneratedBill>[] = [
    {
      key: "invoiceNo",
      header: "Invoice No",
      type: "text",
      sortable: false,
    },
    {
      key: "consumerName",
      header: "Consumer Name",
      type: "text",
      sortable: false,
    },
    {
      key: "accountNumber",
      header: "Account Number",
      type: "text",
      sortable: false,
    },
    {
      key: "consumption",
      header: "Consumption (kWh)",
      type: "custom",
      sortable: false,
      render: (value: number) => value.toString(),
    },
    {
      key: "billAmount",
      header: "Bill Amount",
      type: "custom",
      sortable: false,
      render: (value: number) => `${CURRENCY}${value.toFixed(2)}`,
    },
    {
      key: "outstanding",
      header: "Outstanding",
      type: "custom",
      sortable: false,
      render: (value: number) => (
        <span className={value > 0 ? "text-orange-600 font-medium" : ""}>
          {value > 0 ? `${CURRENCY}${value.toFixed(2)}` : `${CURRENCY}0.00`}
        </span>
      ),
    },
    {
      key: "totalDue",
      header: "Total Due",
      type: "custom",
      sortable: false,
      render: (value: number) => (
        <span className="font-semibold">{`${CURRENCY}${value.toFixed(
          2
        )}`}</span>
      ),
    },
  ];

  // Define actions
  const actions: TableAction[] = [
    {
      label: "View",
      onClick: handleView,
      icon: "Eye",
      variant: "ghost",
    },
    {
      label: "Download",
      onClick: handleDownload,
      icon: "Download",
      variant: "ghost",
    },
  ];

  // Calculate pagination
  const totalItems = bills.length;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startItem = (currentPage - 1) * PAGE_SIZE;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);
  const paginatedData = bills.slice(startItem, endItem);

  if (!open) return null;

  const allChecked = Object.values(checklist).every((val) => val);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto relative">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b z-10 px-6 py-4">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Generated Bills
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Review and approve the generated bills
              </p>
            </div>
            <button
              onClick={onClose}
              className="hover:bg-gray-100 p-1 rounded transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="px-6 py-4 space-y-6">
          {/* Bills Table */}
          <div className="border rounded-lg overflow-hidden">
            <GenericTable
              data={paginatedData}
              columns={columns}
              rowKey="id"
              loading={false}
              actions={actions}
              actionsType="icons"
              emptyMessage="No bills generated"
              pagination={{
                currentPage,
                totalPages,
                totalItems,
                itemsPerPage: PAGE_SIZE,
                startItem: startItem + 1,
                endItem,
              }}
              onPageChange={handlePageChange}
            />
          </div>

          {/* Bill Approval Checklist */}
          <div className="border rounded-lg p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">
              Bill Approval Checklist
            </h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="calculations"
                  checked={checklist.calculations}
                  onCheckedChange={() => handleChecklistChange("calculations")}
                />
                <label
                  htmlFor="calculations"
                  className="text-sm font-medium text-gray-700 cursor-pointer"
                >
                  Bill calculations checked
                </label>
              </div>
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="accuracy"
                  checked={checklist.accuracy}
                  onCheckedChange={() => handleChecklistChange("accuracy")}
                />
                <label
                  htmlFor="accuracy"
                  className="text-sm font-medium text-gray-700 cursor-pointer"
                >
                  Billing data accurate
                </label>
              </div>
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="consent"
                  checked={checklist.consent}
                  onCheckedChange={() => handleChecklistChange("consent")}
                />
                <label
                  htmlFor="consent"
                  className="text-sm font-medium text-gray-700 cursor-pointer"
                >
                  Complete billing process consent
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-white border-t px-6 py-4">
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleApprove} disabled={!allChecked}>
              Approve & Finalize
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
